import { createRootRoute, Outlet, type ErrorComponentProps } from '@tanstack/react-router';
import { useState } from 'react';
import { Button } from '@truecare/ui';

/**
 * Audit M1 (2026-09-01) — branded route-error fallback.
 *
 * The portal previously supplied no `errorComponent`, so any error reaching a
 * route boundary fell through to TanStack Router's built-in dev screen ("Something
 * went wrong! API 429 Too Many Requests" + a raw monospace status), with no brand
 * shell, plain-language message, retry, or way back — the catch-all for every
 * route, shown to a phone-first caregiver or a 50+ family client. Ports the admin
 * app's AdminError pattern: a branded card, human copy, primary Retry + secondary
 * Back, the raw status behind an optional disclosure, and a calm rate-limit case.
 *
 * Deliberately does NOT wrap in a persona layout — the layout reads the current
 * user, which can itself be the error source; re-rendering it risks an error loop.
 */
function PortalError({ error, reset }: ErrorComponentProps) {
  const [showDetails, setShowDetails] = useState(false);
  const raw = error instanceof Error ? error.message : String(error);
  const isRateLimited = /\b429\b|too many requests/i.test(raw);

  return (
    <div className="mx-auto max-w-lg px-6 py-20 text-center">
      <div className="rounded-lg border border-border bg-paper p-8 shadow-sm">
        <h1 className="font-heading text-2xl text-forest">
          {isRateLimited ? 'Just a moment' : 'Something went wrong'}
        </h1>
        <p className="mt-3 font-body text-ink-muted">
          {isRateLimited
            ? "You've done that a few times quickly. Please wait a moment and try again."
            : 'We hit an unexpected problem loading this page. Trying again usually fixes it.'}
        </p>
        <div className="mt-6 flex flex-col justify-center gap-3 sm:flex-row">
          <Button onClick={() => reset()}>Try again</Button>
          <a href="/me">
            <Button variant="secondary">Back to my workspace</Button>
          </a>
        </div>
        <button
          type="button"
          onClick={() => setShowDetails((v) => !v)}
          className="mt-6 text-xs text-ink-muted underline"
        >
          {showDetails ? 'Hide details' : 'Show details'}
        </button>
        {showDetails && (
          <pre className="mt-2 overflow-x-auto rounded-md bg-ink-soft/40 p-3 text-left font-mono text-xs text-ink-muted">
            {raw}
          </pre>
        )}
      </div>
    </div>
  );
}

export const Route = createRootRoute({
  component: () => <Outlet />,
  errorComponent: PortalError,
});
